import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Factory, ChevronRight, Phone, Package, Users, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FAQManufacturing = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      q: 'What is OEM manufacturing?',
      a: 'OEM (Original Equipment Manufacturer) means RSD manufactures products that you sell under YOUR brand name. We produce the item, you brand it. Benefits: No factory investment, proven quality, faster time-to-market. RSD provides logo printing, custom packaging, and certification support for OEM partners.'
    },
    {
      q: 'What is ODM manufacturing?',
      a: 'ODM (Original Design Manufacturer) means RSD both designs AND manufactures products for you. We create new product designs based on your specifications, handle tooling/molds, produce prototypes, and manufacture at scale. Ideal for brands wanting exclusive, unique products not available elsewhere.'
    },
    {
      q: 'What is the difference between OEM and white label?',
      a: 'OEM: Custom branding on existing RSD product designs. White Label: Ready RSD products with your branding added quickly. OEM offers more customization (colors, specs) but longer lead time. White label is faster but limited to existing designs. Both get your branding on quality products.'
    },
    {
      q: 'What is the minimum order quantity (MOQ) for OEM orders?',
      a: 'OEM MOQ varies by product: Data Cables: 5,000-10,000 units, Mobile Chargers: 5,000-10,000 units (varies by wattage), Audio Products: 3,000-5,000 units. For new partners, we offer flexible MOQ on first orders. Volume discounts for orders above 25,000 units.'
    },
    {
      q: 'What is the lead time for OEM/ODM orders?',
      a: 'Standard OEM (existing design + your branding): 15-20 days. Custom ODM (new design): 25-45 days (includes design approval, tooling, sampling). First orders include sample approval process (5-7 days). Repeat orders can be fulfilled in 10-15 days depending on quantity.'
    },
    {
      q: 'What customization options are available for OEM?',
      a: 'Full customization available: Logo printing/embossing, Custom colors (including matte metallic finishes), Custom cable lengths, Custom wattage specifications, Private label packaging design, Retail box customization, Instruction manual with your branding, Warranty cards with your brand.'
    },
    {
      q: 'Does RSD help with product certification (BIS, etc.)?',
      a: 'Yes, we provide BIS certification support for OEM charger partners. Our facility is already BIS certified, and we guide you through the certification process for your brand. We provide all technical documentation, test reports, and compliance assistance needed.'
    },
    {
      q: 'What quality assurance is provided for OEM orders?',
      a: 'OEM orders receive: 100% electrical testing, Protocol verification for fast charging, Durability testing (bend cycles for cables), Visual inspection, Pre-shipment quality report, ISO 9001:2015 certified manufacturing. Defect rate typically under 0.5%.'
    },
    {
      q: 'Can RSD manufacture exclusive/unique product designs?',
      a: 'Yes, through our ODM service. We can develop exclusive products: unique charger shapes/colors, special cable designs (transparent, RGB, rotating heads), custom audio product designs. Exclusivity agreements available for unique designs. Tooling costs may apply for new molds.'
    },
    {
      q: 'What is the payment terms for OEM orders?',
      a: 'Standard terms: 50% advance with order confirmation, 50% before shipment. For established partners with good track record: 30% advance, 70% on delivery. LC (Letter of Credit) accepted for export orders. Credit terms available after relationship building.'
    },
    {
      q: 'Does RSD provide samples before bulk orders?',
      a: 'Yes, samples are provided before bulk production. For existing products: samples available immediately (sample cost + courier charges). For custom OEM/ODM: prototype samples in 7-15 days. Sample cost adjusted against bulk order for confirmed orders.'
    },
    {
      q: 'What is RSD\'s daily production capacity?',
      a: 'Current capacity: Data Cables: 30,000+ units/day, Mobile Chargers: 15,000+ units/day, Car Chargers: 5,000+ units/day, Audio Products: 5,000+ units/day. Total facility capacity: 50,000+ units/day across all categories. Capacity can be scaled for large orders.'
    },
    {
      q: 'Does RSD serve e-commerce sellers (Amazon, Flipkart)?',
      a: 'Yes, many of our partners are Amazon, Flipkart, and Meesho sellers. We provide: FBA-ready packaging, GTIN/barcode printing, Listing-ready product images, Compliance documentation for marketplace requirements. MOQ is flexible for e-commerce sellers.'
    },
    {
      q: 'Can RSD export to other countries?',
      a: 'Yes, RSD exports to multiple countries. We provide: CE marking for EU markets, Export documentation, International shipping support, Custom packaging for export. Current export destinations include Middle East, Africa, and Southeast Asia.'
    },
    {
      q: 'How do I start an OEM partnership with RSD?',
      a: 'Simple process: 1) Contact us with requirements (products, quantities, customization), 2) Receive quote and timeline, 3) Sample approval, 4) Artwork/design confirmation, 5) Production begins, 6) Quality inspection, 7) Delivery. Call +91-9311211889 or email to start discussion.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Manufacturing FAQ - OEM ODM Questions | RSD Accessories</title>
        <meta name="description" content="Learn about OEM ODM manufacturing, MOQ, lead times, customization options, and how to partner with India&apos;s leading mobile accessories manufacturer." />
        <meta name="keywords" content="oem manufacturing faq, odm manufacturing, private label mobile accessories, custom branding, moq wholesale, manufacturing lead time" />
        <link rel="canonical" href="https://rsdaccessories.com/faq/manufacturing" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Breadcrumb */}
        <div className="bg-neutral-100 py-3">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-neutral-500 hover:text-[#FF4D00]">Home</Link>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
              <Link to="/faq" className="text-neutral-500 hover:text-[#FF4D00]">FAQ</Link>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
              <span className="text-neutral-900 font-medium">Manufacturing</span>
            </div>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-br from-neutral-900 to-black py-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <Factory className="w-16 h-16 text-[#FF4D00] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Manufacturing FAQs
            </h1>
            <p className="text-xl text-neutral-300 max-w-2xl mx-auto">
              OEM, ODM, private label, MOQ, lead times - everything about partnering with RSD
            </p>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-8 bg-[#FF4D00]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
              <div>
                <Package className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">50,000+</div>
                <div className="text-sm text-white/80">Daily Capacity</div>
              </div>
              <div>
                <Users className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">100+</div>
                <div className="text-sm text-white/80">OEM Partners</div>
              </div>
              <div>
                <Clock className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">15-20</div>
                <div className="text-sm text-white/80">Days Lead Time</div>
              </div>
              <div>
                <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">ISO</div>
                <div className="text-sm text-white/80">9001:2015</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-8">{faqs.length} Questions About Manufacturing</h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow border border-neutral-100 hover:border-[#FF4D00] transition-colors">
                  <h3 className="text-lg font-bold text-neutral-900 mb-3 flex items-start gap-3">
                    <span className="bg-[#FF4D00] text-white w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm">
                      {idx + 1}
                    </span>
                    {faq.q}
                  </h3>
                  <p className="text-neutral-600 ml-11">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Start Your OEM Partnership?</h2>
            <p className="text-white/90 mb-8">Launch your brand with India's trusted manufacturer</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: +91-9311211889
                </Button>
              </Link>
              <Link to="/become-distributor">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00]">
                  Become a Partner
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQManufacturing;
